import { useMemo, useState } from "react";
import { X, Loader2, AlertCircle, Download } from "lucide-react";
import { useAdminUpgradeAnalysisQuery } from "../api/upgradeApi";

type PaymentFilter = "all" | "paid" | "unpaid";

const PAYMENT_OPTIONS: { value: PaymentFilter; label: string }[] = [
  { value: "all",    label: "All applications" },
  { value: "paid",   label: "Paid only" },
  { value: "unpaid", label: "Unpaid only" },
];

/** Where a paid-but-not-live application actually sits. */
const STAGE_LABELS: Record<string, string> = {
  awaiting_member: "Returned — not reopened",
  reopened:        "Reopened — not resubmitted",
  never_submitted: "Paid — never sent in",
};

/**
 * The upgrade pipeline in one sheet.
 *
 * Aggregated server-side over EVERY application, so the figures are the
 * Institute's position rather than whatever page the admin had loaded. Built
 * around the question the secretariat actually asks — who is holding things up
 * — so the chapter backlog leads and the totals support it.
 */

const naira = (kobo?: number | null) =>
  `₦${Number((kobo ?? 0) / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_payment: "Awaiting payment",
  submitted: "Submitted",
  awaiting_endorsements: "Awaiting endorsements",
  under_review: "With committee",
  approved: "Approved",
  rejected: "Rejected",
  returned: "Returned for correction",
};

/** One headline figure. `tone` carries the reading — amber/rose mean "act". */
function Stat({
  label, value, sub, tone = "plain",
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: "plain" | "good" | "warn" | "bad";
}) {
  const tones = {
    plain: "bg-white border-gray-100 text-gray-900",
    good:  "bg-emerald-50 border-emerald-100 text-emerald-800",
    warn:  "bg-amber-50 border-amber-100 text-amber-800",
    bad:   "bg-rose-50 border-rose-100 text-rose-800",
  } as const;

  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-60">{label}</div>
      <div className="mt-1 text-2xl font-black leading-none">{value}</div>
      {sub && <div className="mt-1 text-[11px] font-semibold opacity-70">{sub}</div>}
    </div>
  );
}

/** A labelled count list — used for the status, grade and route breakdowns. */
function Breakdown({ title, data }: { title: string; data?: Record<string, number> }) {
  const rows = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((n, [, v]) => n + v, 0);

  if (!rows.length) return null;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{title}</div>
      <div className="mt-3 space-y-2">
        {rows.map(([key, count]) => (
          <div key={key}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-gray-700">{STATUS_LABELS[key] ?? key}</span>
              <span className="font-black text-gray-900 tabular-nums">{count}</span>
            </div>
            {/* Proportion, so a long tail reads at a glance. */}
            <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-indigo-400 rounded-full"
                style={{ width: total ? `${Math.max(2, (count / total) * 100)}%` : "0%" }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UpgradeAnalysisModal({ onClose }: { onClose: () => void }) {
  const [payment, setPayment] = useState<PaymentFilter>("all");

  // isFetching, not isLoading — changing the filter refetches, and the sheet
  // should say so rather than showing the previous population's figures as if
  // they were the new ones.
  const { data, isLoading, isFetching, isError, error } = useAdminUpgradeAnalysisQuery({ payment });

  // Memoised so the CSV below is not rebuilt on every render by a fresh []
  // literal.
  const chapters: any[] = useMemo(() => data?.chapters ?? [], [data]);

  /** CSV of the chapter table — what gets forwarded to chapter chairmen. */
  const csv = useMemo(() => {
    if (!chapters.length) return "";
    const head = ["Chapter", "Live applications", "Endorsed", "Awaiting chapter", "Referees short", "Ready for review"];
    const body = chapters.map((c) =>
      [c.chapter, c.total, c.chapter_endorsed, c.awaiting_chapter, c.referees_short, c.ready_for_review].join(","),
    );
    return [head.join(","), ...body].join("\n");
  }, [chapters]);

  const downloadCsv = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // The filter is in the filename — a "paid only" sheet must not be mistaken
    // for the full backlog once it has left the screen.
    a.download = `upgrade-chapter-backlog-${payment}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const e = data?.endorsements ?? {};
  const t = data?.totals ?? {};
  const aging = data?.aging ?? {};
  const withApplicant = data?.with_applicant ?? {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-gray-50 rounded-[28px] shadow-2xl w-full max-w-5xl my-8 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 bg-white border-b border-gray-100">
          <div>
            <h2 className="text-xl font-black text-gray-900 tracking-tight">Upgrade pipeline analysis</h2>
            <p className="text-xs font-semibold text-gray-500 mt-0.5">
              {payment === "paid"
                ? "Applications with a settled fee."
                : payment === "unpaid"
                  ? "Applications whose fee has not settled."
                  : "Every upgrade application, not just this page."}
              {data?.generated_at ? ` As at ${data.generated_at}.` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={payment}
              onChange={(e) => setPayment(e.target.value as PaymentFilter)}
              className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-gray-900/10"
            >
              {PAYMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {isFetching && !isLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            )}
            {chapters.length > 0 && (
              <button
                onClick={downloadCsv}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50"
              >
                <Download className="w-3.5 h-3.5" /> Chapter CSV
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-24 flex flex-col items-center gap-3 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-xs font-bold uppercase tracking-widest">Building the sheet…</p>
          </div>
        ) : isError ? (
          <div className="py-20 px-6 text-center">
            <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-rose-700">
              {(error as any)?.data?.message || "Could not build the analysis."}
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Headline */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Applications" value={t.applications ?? 0} sub={`${t.live ?? 0} live in the pipeline`} />
              <Stat
                label="Ready for committee"
                value={e.ready_for_review ?? 0}
                sub="endorsed + 2 referees"
                tone={(e.ready_for_review ?? 0) > 0 ? "good" : "plain"}
              />
              <Stat
                label="Awaiting chapter"
                value={e.awaiting_chapter ?? 0}
                sub="no endorsement yet"
                tone={(e.awaiting_chapter ?? 0) > 0 ? "warn" : "plain"}
              />
              <Stat
                label="Paid, with applicant"
                value={withApplicant.total ?? 0}
                sub="returned / reopened — not live"
                tone={(withApplicant.total ?? 0) > 0 ? "warn" : "plain"}
              />
              <Stat
                label="Over 90 days"
                value={aging.over_90_days ?? 0}
                sub={aging.oldest_days != null ? `oldest ${aging.oldest_days} days` : undefined}
                tone={(aging.over_90_days ?? 0) > 0 ? "bad" : "plain"}
              />
            </div>

            {/* Referee standing — the second gate into committee review. */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Both referees in" value={e.referees_both ?? 0} tone="good" />
              <Stat label="One referee in" value={e.referees_one ?? 0} tone="warn" />
              <Stat label="No referee yet" value={e.referees_none ?? 0} tone="warn" />
              <Stat label="Referee declined" value={e.referee_rejected ?? 0} tone={(e.referee_rejected ?? 0) > 0 ? "bad" : "plain"} />
              <Stat label="No referees named" value={e.no_referees_named ?? 0} tone={(e.no_referees_named ?? 0) > 0 ? "bad" : "plain"} />
            </div>

            {/* Paid but back with the applicant. Invisible in a status-only
                view, and the likeliest place for money to sit forgotten. */}
            {(withApplicant.total ?? 0) > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/60 overflow-hidden">
                <div className="px-4 py-3 border-b border-amber-100 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                      Paid, but back with the applicant
                    </div>
                    <p className="text-[11px] font-semibold text-amber-800/70 mt-0.5">
                      Fee settled and not counted as live — nobody is chasing these.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                    <span className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-800">
                      {withApplicant.awaiting_member ?? 0} returned
                    </span>
                    <span className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-800">
                      {withApplicant.reopened ?? 0} reopened
                    </span>
                    <span className="px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-800">
                      {withApplicant.never_submitted ?? 0} never sent in
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
                        <th className="px-4 py-2">Member</th>
                        <th className="px-4 py-2">Membership</th>
                        <th className="px-4 py-2">Grade</th>
                        <th className="px-4 py-2">Where it is</th>
                        <th className="px-4 py-2 text-right">Idle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(withApplicant.items ?? []).slice(0, 40).map((r: any) => (
                        <tr key={r.id} className="hover:bg-amber-50/40">
                          <td className="px-4 py-2.5 font-semibold text-gray-800">{r.name || "—"}</td>
                          <td className="px-4 py-2.5 text-gray-500">{r.membership_no || "—"}</td>
                          <td className="px-4 py-2.5 text-gray-500">{r.grade || "—"}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-[10px] font-black uppercase tracking-widest text-amber-800">
                              {STAGE_LABELS[r.stage] ?? r.stage}
                            </span>
                          </td>
                          <td className={`px-4 py-2.5 text-right tabular-nums font-black ${
                            (r.idle_days ?? 0) > 30 ? "text-rose-700" : "text-gray-600"
                          }`}>
                            {r.idle_days != null ? `${r.idle_days}d` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(withApplicant.items?.length ?? 0) > 40 && (
                    <p className="px-4 py-2 text-[11px] text-gray-400">
                      … and {withApplicant.items.length - 40} more.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Chapters — the reason this sheet exists. */}
            <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                  By chapter — worst backlog first
                </div>
              </div>
              {chapters.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-400">No live applications.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
                        <th className="px-4 py-2">Chapter</th>
                        <th className="px-4 py-2 text-right">Live</th>
                        <th className="px-4 py-2 text-right">Endorsed</th>
                        <th className="px-4 py-2 text-right">Awaiting chapter</th>
                        <th className="px-4 py-2 text-right">Referees short</th>
                        <th className="px-4 py-2 text-right">Ready</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {chapters.map((c) => (
                        <tr key={c.chapter} className="hover:bg-gray-50/60">
                          <td className="px-4 py-2.5 font-semibold text-gray-800">{c.chapter}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{c.total}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{c.chapter_endorsed}</td>
                          <td className={`px-4 py-2.5 text-right tabular-nums font-black ${c.awaiting_chapter > 0 ? "text-amber-700" : "text-gray-300"}`}>
                            {c.awaiting_chapter}
                          </td>
                          <td className={`px-4 py-2.5 text-right tabular-nums ${c.referees_short > 0 ? "text-amber-700" : "text-gray-300"}`}>
                            {c.referees_short}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{c.ready_for_review}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Supporting breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Breakdown title="By status" data={t.by_status} />
              <Breakdown title="By target grade" data={t.by_grade} />
              <Breakdown title="By route" data={t.by_route} />
            </div>

            {/* Money + time */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Fees settled" value={data?.payments?.settled ?? 0} sub={naira(data?.payments?.collected_kobo)} tone="good" />
              <Stat label="Fees unsettled" value={data?.payments?.unsettled ?? 0} tone={(data?.payments?.unsettled ?? 0) > 0 ? "warn" : "plain"} />
              <Stat label="Average age" value={`${aging.average_days ?? 0}d`} sub="live applications" />
              <Stat label="Over 30 days" value={aging.over_30_days ?? 0} tone={(aging.over_30_days ?? 0) > 0 ? "warn" : "plain"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
